import { Fragment } from 'react';

import PropTypes from 'prop-types';
import { AvailableCurrencies } from 'utils/clickme';
import { VIEW_DATE_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import { Link } from '@hh.ru/redux-spa-middleware';

import Button, { ButtonAppearance, ButtonIconPosition, ButtonKind } from 'bloko/blocks/button';
import Icon, { IconColor, IconScale, IconView } from 'bloko/blocks/icon';
import Text from 'bloko/blocks/text';

import useTrls from 'hooks/translation/useTrls';

import { AccountsLink } from 'components/Admin/AccountsLink/AccountsLink';
import EmployerLink from 'components/Admin/Invoices/InvoicesTable/Invoice/Account/EmployerLink';
import ManagerLink from 'components/Admin/Invoices/InvoicesTable/Invoice/Account/ManagerLink';
import PayerLink from 'components/Admin/Invoices/InvoicesTable/Invoice/Payer/PayerLink';
import Requisites from 'components/Admin/Invoices/InvoicesTable/Invoice/Requisites';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';

import { InvoicePayerType } from 'store/models/invoices';
import { setInvoiceAccountId, setInvoicePayer } from 'store/models/payers';
import { useDispatch } from 'store/utils';

import 'components/Admin/Payers/Payer/Payer.less';

const Payer = ({
    payerId,
    employerId,
    hhid,
    userId,
    accounts,
    email,
    userName,
    billingDetails,
    agreement,
    hhPayerId,
    setVisibleCreateInvoice,
}) => {
    const dispatch = useDispatch();
    const trl = useTrls();

    const isManualInvoceAvailable = accounts.every((item) => item.currency === AvailableCurrencies.RUR);

    return (
        <TableRow>
            <TableCell>
                <div className="payer-cell">
                    <div>
                        <Text Element="span" strong>
                            {trl('admin.Users.User.employerID')}
                        </Text>
                        <EmployerLink employerId={employerId} />
                    </div>
                    <div>
                        <Text Element="span" strong>
                            {trl('admin.Users.User.hhID')}
                        </Text>
                        {hhid}
                    </div>
                    <div>
                        <Text Element="span" strong>
                            {trl('admin.Users.User.id')}
                        </Text>
                        {userId}
                    </div>
                    <div>
                        <Text Element="span" strong>
                            {trl('admin.Users.User.accountId')}
                        </Text>
                        <AccountsLink accounts={accounts} userId={userId} />
                    </div>
                    <div>
                        <Text Element="span" strong>
                            {trl('admin.Payers.Payer.payerId')}
                        </Text>
                        <PayerLink payerId={payerId} />
                    </div>
                </div>
            </TableCell>
            <TableCell>
                <div className="payer-cell">
                    <div>
                        <Text Element="span" strong>
                            {trl('admin.Users.User.email')}
                        </Text>
                        <ManagerLink managerEmail={email} />
                    </div>
                    <div>
                        <Text Element="span" strong>
                            {trl('admin.Users.User.name')}
                        </Text>
                        {userName}
                    </div>
                </div>
            </TableCell>
            <TableCell>
                <div className="payer-cell">
                    <Text strong>{trl(`InvoiceAndPayerForm.PayerCreate.PayerType.${billingDetails.payer}`)}</Text>
                    {billingDetails.payer !== InvoicePayerType.Natural ? (
                        <Requisites {...billingDetails} />
                    ) : (
                        billingDetails.fio
                    )}
                    {hhPayerId && agreement?.number && agreement?.createdAt ? (
                        <Fragment>
                            <div>
                                <Text Element="span" strong>
                                    {trl('admin.Payers.Payer.agreement')}
                                </Text>
                                {`№${agreement.number} ${formatDate(agreement.createdAt, VIEW_DATE_FORMAT)}`}
                            </div>
                            <div>
                                <Text Element="span" strong>
                                    {trl('admin.Payers.Payer.hhPayerID')}
                                </Text>
                                {hhPayerId}
                            </div>
                        </Fragment>
                    ) : (
                        <Text strong>{trl('admin.Payers.Payer.not.agreement')}</Text>
                    )}
                </div>
            </TableCell>
            <TableCell>
                <div className="payer-action">
                    <Button
                        Element={Link}
                        to={`/new/admin/payers/${payerId}`}
                        appearance={ButtonAppearance.Outlined}
                        iconPosition={ButtonIconPosition.Left}
                        icon={<Icon view={IconView.Gear} scale={IconScale.Small} />}
                    >
                        {trl('admin.Users.User.edit')}
                    </Button>
                </div>
                {isManualInvoceAvailable && (
                    <div className="payer-action">
                        <Button
                            iconPosition={ButtonIconPosition.Left}
                            icon={<Icon view={IconView.PenSquare} initial={IconColor.White} scale={IconScale.Small} />}
                            kind={ButtonKind.Primary}
                            onClick={() => {
                                dispatch(setInvoiceAccountId(accounts[0].id));
                                dispatch(setInvoicePayer({ payerId, userId, userName, email, accounts }));
                                setVisibleCreateInvoice(true);
                            }}
                            data-qa="payer-create-invoice"
                        >
                            {trl('admin.Payers.Payer.createInvoice')}
                        </Button>
                    </div>
                )}
            </TableCell>
        </TableRow>
    );
};

Payer.propTypes = {
    payerId: PropTypes.number,
    employerId: PropTypes.number,
    hhid: PropTypes.number,
    userId: PropTypes.number,
    accounts: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            title: PropTypes.string,
            main: PropTypes.bool,
        })
    ),
    email: PropTypes.string,
    userName: PropTypes.string,
    billingDetails: PropTypes.shape({
        fio: PropTypes.string,
        payer: PropTypes.string,
        title: PropTypes.string,
        address: PropTypes.string,
        inn: PropTypes.string,
        kpp: PropTypes.string,
        post: PropTypes.string,
    }),
    agreement: PropTypes.shape({
        number: PropTypes.string,
        createdAt: PropTypes.string,
    }),
    hhPayerId: PropTypes.string,
    setVisibleCreateInvoice: PropTypes.func,
};

export default Payer;
